<template>
  <Field
    v-slot="{ errors, meta, field }"
    :name="vid"
    :rules="rules"
    :vid="vid"
    class="column is-12"
  >
    <b-field
      :label="label"
      :message="resolveMessage(errors)"
      :type="resolveType(errors, meta)"
      class="file is-primary column is-offset-1 is-10"
      :class="helperMessage ? `oa-input--${helperKind}` : ''"
    >
      <template #label>
        <span
          class="label"
          :aria-label="label"
        >{{ label }}</span>
        <span
          v-if="required"
          class="required"
          :aria-label="$t('ponctuation.star')"
        >{{ $t("ponctuation.star") }}</span>
      </template>
      <b-taginput
        v-if="multiplicity === 'MANY'"
        v-model="textValue"
        :aria-label="textValue"
        required
        type="textarea"
        v-bind="field"
        @update:model-value="updateValue"
      />
      <b-input
        v-else
        v-model="textValue"
        required
        :value="textValue"
        :aria-label="textValue"
        :maxlength="maxLength"
        type="textarea"
        @update:model-value="updateValue"
        @input="field.onChange($event)"
        @blur="field.onBlur"
      />
      <!--
        Slot #message de b-field : Buefy y rend un <p class="help">
        positionné dans le .field au même niveau que .control et
        .help.counter ( cf retour user ) . On y injecte une ligne
        icône + texte custom qui remplace le rendu auto Buefy ( cf
        resolveMessage = "" quand helperMessage est piloté ) , sans
        casser la structure DOM attendue . Le slot prend le pas sur
        le prop :message ; il garantit aussi un positionnement
        stable indépendant des évolutions internes Buefy , là où une
        balise externe au b-field traînait avec un gros gap .
      -->
      <template
        v-if="helperMessage"
        #message
      >
        <span
          class="oa-input-helper"
          :class="`oa-input-helper--${helperKind}`"
          role="alert"
          aria-live="polite"
        >
          <span
            class="oa-input-helper__icon"
            aria-hidden="true"
          >{{ helperKind === 'is-success' ? '✓' : '!' }}</span>
          <span class="oa-input-helper__text">{{ helperMessage }}</span>
        </span>
      </template>
    </b-field>
  </Field>
</template>

<script lang="ts">
import { Field } from "vee-validate";
import { computed, ref, watch } from "vue";
import { BField, BInput, BTaginput } from "buefy";

export default {
  name: "OreInputText",
  components: {
    BField,
    BInput,
    BTaginput,
    Field,
  },
  props: {
    checker: {
      type: Object,
      required: false,
    },
    value: {
      required: true,
    },
    label: {
      type: String,
    },
    vid: {
      type: String,
    },
    maxLength: {
      type: Number,
      required: false,
    },
    /**
     * Message d'aide ou d'erreur affiché sous le champ , prioritaire
     * sur les erreurs vee-validate quand non-vide . Utilisé par les
     * parents qui pilotent leur propre validation custom ( ex
     * commentValidator sur la demande de droits d'accès ) .
     */
    helperMessage: {
      type: String,
      required: false,
      default: "",
    },
    /**
     * Force le type Buefy du b-field ( "is-danger" / "is-success" ) .
     * Quand non-vide , l'emporte sur la détection auto fondée sur
     * vee-validate + longueur de saisie . Permet aux parents
     * d'aligner précisément la couleur des bords / icônes avec leur
     * propre logique métier .
     */
    helperType: {
      type: String,
      required: false,
      default: "",
    },
  },
  emits: ["update:value"],
  setup(props, ctx) {
    const val = ref(props.value);
    watch(
      () => props.value,
      (newValue) => {
        if (newValue) {
          val.value = newValue;
        }
      },
      { immediate: true },
    );

    const textValue = computed({
      get: () => {
        return val.value;
      },
      set: (newValue) => {
        val.value = newValue;
        updateValue(newValue);
      },
    });

    function updateValue(event) {
      if (typeof event == "object" && !Array.isArray(event) && event.target) {
        event = event.target.value;
      }
      ctx.emit("update:value", event);
    }

    /**
     * Message à donner au composant b-field de Buefy . Quand le parent
     * fournit un {@code helperMessage} , on retourne "" pour supprimer
     * l'auto-help ( et donc l'icône auto associée ) , parce qu'on
     * affiche notre propre ligne icône + texte juste après le b-field
     * pour garantir un alignement horizontal stable . Fallback sur les
     * erreurs vee-validate quand aucun helper custom n'est piloté ,
     * ce qui préserve la compatibilité ascendante .
     */
    function resolveMessage(errors) {
      if (props.helperMessage) return "";
      return errors;
    }

    /**
     * Sélecteur de "kind" pour la ligne d'aide custom : "is-danger"
     * ou "is-success" . Pilote la couleur ET le glyphe ( "!" vs "✓" )
     * dans le template . Suit la même règle que {@link resolveType} -
     * priorité au {@code helperType} explicite du parent .
     */
    const helperKind = computed<"is-danger" | "is-success">(() => {
      if (props.helperType === "is-success") return "is-success";
      return "is-danger";
    });

    /**
     * Sélecteur de type Buefy du b-field . Quand le parent pilote un
     * {@code helperMessage} , on rend type vide pour empêcher Buefy
     * d'injecter sa propre icône de validation à droite du textarea
     * ( cf {@code has-icons-right} ajouté automatiquement quand type
     * est is-danger / is-success ) , évitant l'icône orpheline visible
     * au-dessus de notre ligne d'aide custom . Le bord coloré
     * ( rouge / vert ) est alors reconstitué côté CSS via la classe
     * {@code oa-input--is-danger / is-success} appliquée au b-field .
     * Sans helper : détection auto historique préservée , back-compat
     * pour tous les autres usages d'OreInputText .
     */
    function resolveType(errors, meta) {
      if (props.helperMessage) {
        return {};
      }
      if (props.helperType === "is-danger" || props.helperType === "is-success") {
        return { [props.helperType]: true };
      }
      return {
        "is-danger": (errors && errors.length > 0) || val.value?.length === 0,
        "is-success": meta?.valid && val.value?.length > 0,
      };
    }

    return {
      updateValue,
      val,
      textValue,
      resolveMessage,
      resolveType,
      helperKind,
    };
  },
  computed: {
    required: {
      get() {
        return this?.checker && (this.checker?.params?.required || this?.checker?.required);
      },
    },
    multiplicity: {
      get() {
        return this.checker && this.checker.multiplicity === "MANY";
      },
    },
    rules: {
      get() {
        let rules = [];
        if (this.checker) {
          if (this.checker.type === "BooleanChecker" && this.checker.expression) {
            if (this.checker.pattern) {
              const regexpPattern = "^" + this.checker.pattern + "$";
              rules.push("regexp:" + regexpPattern);
            }
          }
          if (this.checker.required) {
            rules.push("required");
          }
        }
        return rules.join("|");
      },
    },
  },
};
</script>

<style lang="scss" scoped>
.required {
  color: $danger;
  padding-right: 5px;
  font-size: 150%;
}
.label {
  display: contents !important;
}

/*
 * Quand le parent pilote {@code helperMessage} , le b-field a son type
 * vidé ( cf resolveType ) pour empêcher Buefy d'injecter une icône
 * de validation automatique . On reconstitue ici le bord coloré
 * ( rouge ou vert ) via une classe utilitaire posée sur le b-field ,
 * pour conserver le signal visuel sans avoir l'icône orpheline .
 *
 * Les sélecteurs {@code :focus} et {@code :hover} sont explicites
 * parce que Buefy applique des styles {@code :focus} ( bord teal /
 * primary ) qui , sans cette neutralisation , masqueraient le bord
 * d'erreur pendant que l'utilisateur tape .
 */
:deep(.oa-input--is-danger) .textarea,
:deep(.oa-input--is-danger) .input,
:deep(.oa-input--is-danger) .textarea:focus,
:deep(.oa-input--is-danger) .input:focus,
:deep(.oa-input--is-danger) .textarea:hover,
:deep(.oa-input--is-danger) .input:hover {
  border-color: $danger !important;
  box-shadow: none !important;
}
:deep(.oa-input--is-success) .textarea,
:deep(.oa-input--is-success) .input,
:deep(.oa-input--is-success) .textarea:focus,
:deep(.oa-input--is-success) .input:focus,
:deep(.oa-input--is-success) .textarea:hover,
:deep(.oa-input--is-success) .input:hover {
  border-color: $success !important;
  box-shadow: none !important;
}

/*
 * Layout : on veut l'aide ( icône + texte ) sur la même ligne que le
 * compteur de caractères "{n}/{max}" rendu automatiquement par Buefy .
 * Sans intervention , Buefy empile les deux <p class="help"> en
 * colonne ( counter au-dessus , notre message en dessous ) .
 *
 * Solution : pinner le {@code .help.counter} en absolute en bas à
 * droite du {@code .field} , et laisser notre {@code .help} en flux
 * normal avec un padding-right pour réserver la place du counter .
 * Les deux finissent visuellement sur la même ligne sans casser la
 * structure DOM Buefy ( zéro DOM hack , uniquement du CSS ) .
 */
:deep(.oa-input--is-danger),
:deep(.oa-input--is-success) {
  position: relative;
}
:deep(.oa-input--is-danger) > .help.counter,
:deep(.oa-input--is-success) > .help.counter {
  position: absolute;
  right: 0.5rem;
  bottom: 0;
  margin: 0;
  padding: 0;
}
:deep(.oa-input--is-danger) > .help:not(.counter),
:deep(.oa-input--is-success) > .help:not(.counter) {
  padding-right: 5rem;
  margin: 0;
}

/*
 * Ligne d'aide custom : icône + texte côte-à-côte , rendue dans le
 * slot #message de b-field ( donc dans <p class="help"> au même
 * niveau que .control et .help.counter ) . `inline-flex` parce que
 * le parent <p class="help"> est inline-level ; on garantit la mise
 * en flex row tout en restant compatible avec le rendu Buefy .
 */
.oa-input-helper {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  line-height: 1.3;
  flex-wrap: wrap;
}
.oa-input-helper__icon {
  flex: 0 0 auto;
  width: 1.25rem;
  height: 1.25rem;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  font-weight: 700;
  font-size: 0.85rem;
  line-height: 1;
}
.oa-input-helper__text {
  flex: 1 1 auto;
  min-width: 0;
}
.oa-input-helper--is-danger {
  color: $danger;
}
.oa-input-helper--is-danger .oa-input-helper__icon {
  background: $danger;
}
.oa-input-helper--is-success {
  color: $success;
}
.oa-input-helper--is-success .oa-input-helper__icon {
  background: $success;
}
</style>
